'use strict';

import { state, saveAuth, homeRoute } from './state.js';
import { api, apiFetch } from './api.js';
import { el, mount, navigate } from './dom.js';
import { loadInitialData } from './shell.js';
import { setupPushNotifications } from './pwa.js';
import { svgIcon } from './icons.js';
import { publicStatusPill } from './public-status.js';
import { takeReturnRoute } from './return-route.js';

// Wraps a password <input> with a show/hide toggle button.
// Returns { input, wrap } — use wrap in the DOM, input for .value access.
function passwordField({ id, name, placeholder, autocomplete }) {
  const input = el('input', { type: 'password', required: 'required', placeholder, autocomplete,
    ...(id ? { id } : {}), ...(name ? { name } : {}) });
  const btn = el('button', { type: 'button', className: 'pw-toggle', 'aria-label': 'Show password',
    onclick() {
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      btn.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
      btn.replaceChildren(svgIcon(show ? 'eye-slash' : 'eye'));
    },
  }, svgIcon('eye'));
  return { input, wrap: el('div', { className: 'pw-input-wrap' }, input, btn) };
}

export function providerLabel(p) {
  return { google: 'Google', microsoft: 'Microsoft', facebook: 'Facebook', apple: 'Apple', yahoo: 'Yahoo' }[p] || (p.charAt(0).toUpperCase() + p.slice(1));
}

// Returns the standard 4-color Google "G" SVG per Google's branding guidelines.
function googleGSVG() {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('width', '18'); svg.setAttribute('height', '18'); svg.setAttribute('viewBox', '0 0 48 48');
  [
    ['#EA4335', 'M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'],
    ['#4285F4', 'M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'],
    ['#FBBC05', 'M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'],
    ['#34A853', 'M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'],
  ].forEach(([fill, d]) => {
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('fill', fill); path.setAttribute('d', d);
    svg.appendChild(path);
  });
  return svg;
}

// Returns the Apple logo SVG per Apple's Sign In with Apple guidelines.
function appleLogoSVG() {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('width', '15'); svg.setAttribute('height', '18'); svg.setAttribute('viewBox', '0 0 814 1000');
  const path = document.createElementNS(ns, 'path');
  path.setAttribute('fill', 'currentColor');
  path.setAttribute('d', 'M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57.8-155.5-127.4c-58.3-81.7-105.5-209.4-105.5-330.3 0-194.3 126.4-297.4 250.8-297.4 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.7zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8.6 15.6 1.3 18.2 2.6.6 6.4 1.3 10.2 1.3 45.4 0 103.5-30.4 139.5-71.4z');
  svg.appendChild(path);
  return svg;
}

// Builds a branded OAuth button. Google and Apple get official logos and required text;
// all other providers get a plain outline button with the provider name.
function buildOAuthBtn(p, brandedLabel, onclick) {
  if (p === 'google') {
    return el('button', { type: 'button', className: 'btn btn-full oauth-btn oauth-btn-google', onclick },
      googleGSVG(), brandedLabel);
  }
  if (p === 'apple') {
    return el('button', { type: 'button', className: 'btn btn-full oauth-btn oauth-btn-apple', onclick },
      appleLogoSVG(), brandedLabel.replace('Google', 'Apple'));
  }
  return el('button', { type: 'button', className: 'btn btn-outline btn-full oauth-btn', onclick },
    providerLabel(p));
}

export function loginLogo() {
  return el('div', { className: 'login-logo' },
    el('img', { src: '/images/whisper-icon.png', alt: 'Field Whisper', className: 'login-logo-img' }),
    // The stacked wordmark in both theme variants; CSS shows one. The hidden
    // one is display:none, so screen readers skip it.
    el('h1', { className: 'login-wordmark' },
      el('img', { src: '/images/wordmark-stacked.svg', alt: 'Field Whisper', className: 'login-wordmark-light' }),
      el('img', { src: '/images/wordmark-stacked-dark.svg', alt: 'Field Whisper', className: 'login-wordmark-dark' }),
    ),
    el('p', {}, 'WYSA Field Management'),
  );
}

// Link to the iOS app on the App Store. Renders Apple's official black badge
// by default and swaps to the white variant under dark mode (handled in CSS).
// Artwork is unmodified per Apple's App Store Marketing Guidelines.
export function appStoreLink() {
  return el('a', {
    className: 'app-store-link',
    href: 'https://apps.apple.com/us/app/wysa-field-whisper/id6761733403',
    target: '_blank',
    rel: 'noopener',
  },
    el('span', { className: 'app-store-link-label' }, 'Also available on iOS'),
    el('img', { src: '/images/app-store-badge-black.svg', alt: 'Download on the App Store',
      className: 'app-store-badge-img app-store-badge-light', width: '120', height: '40' }),
    el('img', { src: '/images/app-store-badge-white.svg', alt: '',
      className: 'app-store-badge-img app-store-badge-dark', width: '120', height: '40', 'aria-hidden': 'true' }),
  );
}

export function renderPendingApproval() {
  mount(el('div', { className: 'login-page' },
    el('div', { className: 'login-card' },
      loginLogo(),
      el('div', { className: 'reg-success' },
        el('p', {}, 'Your account has been created and is pending approval by the Fields Director. You will be notified once it is approved.'),
        el('button', { className: 'btn btn-outline btn-full', style: 'margin-top:16px;',
          onclick() { navigate('/login'); }
        }, 'Back to Sign In'),
      ),
    ),
  ));
}

// navigateAfterSignIn goes to the deep link the user opened while signed out
// (remembered by the router), else to their home page. The login page renders
// under the requested hash, so the target can equal the current hash; setting
// location.hash to itself fires no hashchange, so the route is re-run by hand.
export function navigateAfterSignIn() {
  const target = takeReturnRoute() || homeRoute();
  if (location.hash === '#' + target) window.dispatchEvent(new HashChangeEvent('hashchange'));
  else navigate(target);
}

export async function renderLogin() {
  const errEl = el('div', { className: 'form-error', id: 'login-err' });
  const emailI = el('input', { type: 'email', id: 'email', name: 'email', required: 'required',
    placeholder: 'you@example.com', autocomplete: 'email' });
  const { input: passI, wrap: passWrap } = passwordField({ id: 'password', name: 'password', autocomplete: 'current-password' });
  const submitBtn = el('button', { type: 'submit', className: 'btn btn-primary btn-full' }, 'Sign In');

  // OAuth container goes above the password form so OAuth users see their button first.
  const oauthContainer = el('div', { id: 'oauth-container' });

  const form = el('form', { className: 'login-form',
    async onsubmit(e) {
      e.preventDefault();
      submitBtn.disabled = true;
      submitBtn.textContent = 'Signing in…';
      errEl.textContent = '';
      oauthContainer.classList.remove('oauth-highlight');
      try {
        const data = await api.login(emailI.value.trim(), passI.value);
        saveAuth(data.access_token, data.refresh_token);
        await loadInitialData();
        navigateAfterSignIn();
        setupPushNotifications();
        if (window.__reconnectSSE) window.__reconnectSSE();
      } catch (err) {
        if (err.code === 'oauth_only') {
          errEl.textContent = 'This account uses social sign-in. Use the button above to log in.';
          oauthContainer.classList.add('oauth-highlight');
        } else {
          errEl.textContent = err.message || 'Login failed.';
        }
        submitBtn.disabled = false;
        submitBtn.textContent = 'Sign In';
      }
    }
  },
    el('div', { className: 'form-group' }, el('label', { for: 'email' }, 'Email'), emailI),
    el('div', { className: 'form-group' },
      el('div', { style: 'display:flex;justify-content:space-between;align-items:baseline;' },
        el('label', { for: 'password' }, 'Password'),
        el('a', { href: '#/forgot-password', className: 'forgot-link',
          onclick(e) { e.preventDefault(); navigate('/forgot-password'); }
        }, 'Forgot password?'),
      ),
      passWrap,
    ),
    errEl,
    submitBtn,
  );

  mount(el('div', { className: 'login-page' },
    publicStatusPill(),
    el('div', { className: 'login-card' },
      loginLogo(),
      oauthContainer,
      form,
      el('div', { className: 'login-footer' },
        el('span', {}, 'New to WYSA? '),
        el('a', { href: '#/register', onclick(e) { e.preventDefault(); navigate('/register'); } }, 'Request an account'),
      ),
    ),
    appStoreLink(),
    el('div', { className: 'login-copyright' }, '\u00A9 2026 Jakub Moskal'),
  ));

  setTimeout(() => emailI.focus(), 50);

  // Load configured OAuth providers and inject buttons above the password form.
  try {
    const data = await apiFetch('GET', '/auth/providers');
    const providers = data?.providers || [];
    if (providers.length > 0) {
      oauthContainer.replaceChildren(
        el('div', { className: 'oauth-buttons' },
          ...providers.map(p => buildOAuthBtn(p, 'Sign in with Google', async () => {
            errEl.textContent = '';
            oauthContainer.classList.remove('oauth-highlight');
            try {
              const d = await apiFetch('GET', '/auth/oauth/' + p);
              location.href = d.redirect_url;
            } catch (err) {
              errEl.textContent = err.message || 'OAuth sign-in failed.';
            }
          })),
        ),
        el('div', { className: 'oauth-divider' }, el('span', {}, 'or sign in with email')),
      );
    }
  } catch { /* providers endpoint failed — no OAuth buttons shown */ }
}

export async function renderRegister() {
  const errEl = el('div', { className: 'form-error', id: 'reg-err' });
  const nameI = el('input', { type: 'text', required: 'required', name: 'name',
    placeholder: 'Your full name', autocomplete: 'name' });
  const emailI = el('input', { type: 'email', required: 'required', name: 'email',
    placeholder: 'you@example.com', autocomplete: 'email' });
  const { input: passI, wrap: passWrap } = passwordField({ id: 'reg-password', name: 'password', placeholder: 'At least 8 characters', autocomplete: 'new-password' });
  const submitBtn = el('button', { type: 'submit', className: 'btn btn-primary btn-full' }, 'Request Account');

  const form = el('form', { className: 'login-form',
    async onsubmit(e) {
      e.preventDefault();
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';
      errEl.textContent = '';
      try {
        await apiFetch('POST', '/auth/register', {
          name: nameI.value.trim(),
          email: emailI.value.trim(),
          password: passI.value,
        });
        mount(el('div', { className: 'login-page' },
          el('div', { className: 'login-card' },
            loginLogo(),
            el('div', { className: 'reg-success' },
              el('p', {}, 'Account request submitted. The Fields Director will review and approve your account.'),
              el('button', { className: 'btn btn-outline btn-full', style: 'margin-top:16px;',
                onclick() { navigate('/login'); }
              }, 'Back to Sign In'),
            ),
          ),
        ));
      } catch (err) {
        errEl.textContent = err.message || 'Registration failed.';
        submitBtn.disabled = false;
        submitBtn.textContent = 'Request Account';
      }
    }
  },
    el('div', { className: 'form-group' }, el('label', {}, 'Full Name'), nameI),
    el('div', { className: 'form-group' }, el('label', {}, 'Email'), emailI),
    el('div', { className: 'form-group' }, el('label', {}, 'Password'), passWrap),
    errEl,
    submitBtn,
  );

  const oauthContainer = el('div', { id: 'oauth-container' });

  mount(el('div', { className: 'login-page' },
    el('div', { className: 'login-card' },
      loginLogo(),
      el('h2', { className: 'reg-title' }, 'Request an Account'),
      oauthContainer,
      form,
      el('div', { className: 'login-footer' },
        el('span', {}, 'Already have an account? '),
        el('a', { href: '#/login', onclick(e) { e.preventDefault(); navigate('/login'); } }, 'Sign In'),
      ),
    ),
    el('div', { className: 'login-copyright' }, '\u00A9 2026 Jakub Moskal'),
  ));

  setTimeout(() => nameI.focus(), 50);

  // Load configured OAuth providers and inject buttons above the manual form.
  try {
    const data = await apiFetch('GET', '/auth/providers');
    const providers = data?.providers || [];
    if (providers.length > 0) {
      oauthContainer.replaceChildren(
        el('div', { className: 'oauth-buttons' },
          ...providers.map(p => buildOAuthBtn(p, 'Sign up with Google', async () => {
            errEl.textContent = '';
            try {
              const d = await apiFetch('GET', '/auth/oauth/' + p);
              location.href = d.redirect_url;
            } catch (err) {
              errEl.textContent = err.message || 'OAuth sign-up failed.';
            }
          })),
        ),
        el('div', { className: 'oauth-divider' }, el('span', {}, 'or sign up with email')),
      );
    }
  } catch { /* providers endpoint failed — no OAuth buttons shown */ }
}

export function renderForgotPassword() {
  const errEl  = el('div', { className: 'form-error' });
  const infoEl = el('div', { className: 'form-info', style: 'font-size:13px;color:var(--muted);min-height:18px;' });
  const emailI = el('input', { type: 'email', required: 'required',
    placeholder: 'you@example.com', autocomplete: 'email' });
  const submitBtn = el('button', { type: 'submit', className: 'btn btn-primary btn-full' }, 'Send Reset Link');

  const form = el('form', { className: 'login-form',
    async onsubmit(e) {
      e.preventDefault();
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';
      errEl.textContent = '';
      infoEl.textContent = '';
      try {
        await api.forgotPassword(emailI.value.trim());
        infoEl.textContent = 'If an account exists for that email, a reset link has been sent. Check your inbox.';
        emailI.value = '';
      } catch {
        // Always show the generic message to prevent enumeration.
        infoEl.textContent = 'If an account exists for that email, a reset link has been sent. Check your inbox.';
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Send Reset Link';
      }
    }
  },
    el('div', { className: 'form-group' }, el('label', {}, 'Email'), emailI),
    errEl,
    infoEl,
    submitBtn,
  );

  mount(el('div', { className: 'login-page' },
    el('div', { className: 'login-card' },
      loginLogo(),
      el('h2', { className: 'reg-title' }, 'Reset Password'),
      el('p', { style: 'font-size:13px;color:var(--muted);margin-bottom:16px;' },
        'Enter your email address and we\'ll send you a link to reset your password.'),
      form,
      el('div', { className: 'login-footer' },
        el('a', { href: '#/login', onclick(e) { e.preventDefault(); navigate('/login'); } }, 'Back to Sign In'),
      ),
    ),
  ));

  setTimeout(() => emailI.focus(), 50);
}

export async function renderResetPassword(token) {
  if (!token) {
    mount(el('div', { className: 'login-page' },
      el('div', { className: 'login-card' },
        loginLogo(),
        el('p', { style: 'color:var(--danger);font-size:14px;margin-bottom:16px;' },
          'This reset link is missing a token.'),
        el('button', { className: 'btn btn-outline btn-full',
          onclick() { navigate('/forgot-password'); }
        }, 'Request a new link'),
      ),
    ));
    return;
  }

  const errEl    = el('div', { className: 'form-error' });
  const { input: newPwI, wrap: newPwWrap } = passwordField({ name: 'new-password', placeholder: '8+ characters', autocomplete: 'new-password' });
  const { input: confirmI, wrap: confirmWrap } = passwordField({ name: 'confirm-password', placeholder: 'Repeat new password', autocomplete: 'new-password' });
  const submitBtn = el('button', { type: 'submit', className: 'btn btn-primary btn-full' }, 'Set New Password');

  const form = el('form', { className: 'login-form',
    async onsubmit(e) {
      e.preventDefault();
      errEl.textContent = '';
      if (newPwI.value.length < 8) {
        errEl.textContent = 'Password must be at least 8 characters.';
        return;
      }
      if (newPwI.value !== confirmI.value) {
        errEl.textContent = 'Passwords do not match.';
        return;
      }
      submitBtn.disabled = true;
      submitBtn.textContent = 'Setting password…';
      try {
        const data = await api.resetPassword(token, newPwI.value);
        saveAuth(data.access_token, data.refresh_token);
        await loadInitialData();
        navigate(homeRoute());
        setupPushNotifications();
        if (window.__reconnectSSE) window.__reconnectSSE();
      } catch (err) {
        errEl.textContent = err.message || 'Password reset failed.';
        submitBtn.disabled = false;
        submitBtn.textContent = 'Set New Password';
      }
    }
  },
    el('div', { className: 'form-group' }, el('label', {}, 'New Password'), newPwWrap),
    el('div', { className: 'form-group' }, el('label', {}, 'Confirm Password'), confirmWrap),
    errEl,
    submitBtn,
  );

  mount(el('div', { className: 'login-page' },
    el('div', { className: 'login-card' },
      loginLogo(),
      el('h2', { className: 'reg-title' }, 'Set New Password'),
      form,
    ),
  ));

  setTimeout(() => newPwI.focus(), 50);
}
